import { CustomEventName } from '@mb/lib/customEvents';
import { MutationOptions, useMutation } from '@tanstack/react-query';

import { ENDPOINT, MUTATION_KEY, apiClient } from '../../../shared/api';

export interface SignInParams {
  username: string;
  password: string;
  redirect?: string;
}

export interface SignInCustomerData {
  id: number;
  email: string;
  email_sha256: string;
  group_id: number;
  job_type_label?: string | false;
  area_spec_label?: string | false;
  company_name?: string;
  university_school_name?: string;
  test_user: string;
}

export interface SignInSuccessResult {
  status: 'success';
  customer: SignInCustomerData;
  redirect_url?: string;
}

export interface SignInFailedResult {
  status?: 'failed';
  message?: string;
  redirect_url?: string;
}

export type SignInResult = SignInSuccessResult | SignInFailedResult;

export const signIn = async (params: SignInParams): Promise<SignInResult> => {
  const { redirect, ...requestData } = params;

  const { data } = await apiClient.post<SignInResult>(
    ENDPOINT.SIGN_IN,
    requestData,
    { params: { redirect } },
  );

  return data;
};

export interface UseSignInMutationOptions
  extends MutationOptions<SignInResult, unknown, SignInParams, unknown> {
  onSuccessResult?(result: SignInSuccessResult): void;
  onFailedResult?(result: SignInFailedResult): void;
}

export const useSignInMutation = (options: UseSignInMutationOptions = {}) => {
  const { onSuccessResult, onFailedResult, ...mutationOptions } = options;

  return useMutation({
    mutationKey: [MUTATION_KEY.SIGN_IN],
    mutationFn: signIn,
    ...mutationOptions,
    onSuccess: (result, params, context) => {
      if (result.status === 'success') {
        window.dispatchEvent(
          new CustomEvent(CustomEventName.RELOAD_CUSTOMER_SECTION_DATA, {
            detail: result.customer,
          }),
        );

        onSuccessResult?.(result);
      } else {
        onFailedResult?.(result);
      }

      options?.onSuccess?.(result, params, context);
    },
  });
};
